import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';

import { NotificationsStoreService } from '@notifications/store/services/notifications-store.service';
import { GoogleAnalyticsStoreService } from '@core-layout/app/store/services/google-analytics-store.service';
import { NotificationNavigateParams } from '@notifications/models/notification-navigate-params';
import { NOTIFICATION_TARGET_SCROLL_TO } from '@notifications/constants/notifications.constants';
import { Counters } from '@notifications/models/counters';
import { RpcTabItem } from '@core-controls/components/rpc-tabs/models/rpc-tab-item';
import { NotificationTab } from '@notifications/enums/notification-tab.enum';
import { RpcSelectControltOptions } from '@core-controls/components/rpc-select/models/rpc-select-controlt-options';
import { RpcSelectOption } from '@core-controls/components/rpc-select/models/rpc-select-option';
import { MatchMediaService } from '@media/services/match-media.service';
import { HybridService } from 'app/modules/core-modules/hybrid/hybrid.service';
import { Notifications } from '@notifications/models/notifications';
import { RpcButtonComponent } from '../../../core-modules/controls/components/rpc-button/rpc-button.component';
import { RpcSlideToggleComponent } from '../../../core-modules/controls/components/rpc-slide-toggle/rpc-slide-toggle.component';
import { RpcSelectComponent } from '../../../core-modules/controls/components/rpc-select/rpc-select.component';
import { NotificationListComponent } from '../notification-list/notification-list.component';
import { RpcTabsComponent } from '../../../core-modules/controls/components/rpc-tabs/rpc-tabs.component';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RpcButtonComponent, RpcSlideToggleComponent, RpcSelectComponent, NotificationListComponent, RpcTabsComponent, AsyncPipe, TranslateModule]
})
export class NotificationsComponent {
    @Output() public readonly notificationSourceNavigated = new EventEmitter<NotificationNavigateParams>();
    @Output() public readonly closed = new EventEmitter<void>();

    public readonly notificationTargetScrollTo = NOTIFICATION_TARGET_SCROLL_TO;
    public readonly notificationTab = NotificationTab;

    public readonly isMobile$ = this.matchMediaService.onMediaChange.pipe(map(mediaQuery => mediaQuery === 'xs' || mediaQuery === 'sm' || mediaQuery === 'md'));
    public readonly isHybrid$ = this.hybridService.isHybrid$;

    public readonly baseTabsInfo: (RpcTabItem<NotificationTab> & { counterKey: keyof Counters })[] = [
        { text: 'NOTIFICATIONS.TITLES.ALL', value: NotificationTab.All, counterKey: 'total' },
        { text: 'NOTIFICATIONS.TITLES.MESSAGES', value: NotificationTab.Messages, counterKey: 'messages' },
        { text: 'NOTIFICATIONS.TITLES.APPOINTMENTS', value: NotificationTab.Appointments, counterKey: 'appointments' },
        { text: 'NOTIFICATIONS.TITLES.LISTINGS', value: NotificationTab.Listings, counterKey: 'listings' },
    ];

    public readonly indexNotificationsListMap = new Map<NotificationTab, Observable<Notifications>>([
        [NotificationTab.All, this.notificationsStoreService.allNotitifications$],
        [NotificationTab.Messages, this.notificationsStoreService.messageNotitifications$],
        [NotificationTab.Appointments, this.notificationsStoreService.appointmentNotitifications$],
        [NotificationTab.Listings, this.notificationsStoreService.listingNotitifications$],
    ]);

    public readonly isShowOnlyUnviewedNotifications$ = this.notificationsStoreService.isShowOnlyUnviewedNotifications$;
    public readonly flatNotifications$ = this.notificationsStoreService.flatNotifications$;
    public readonly tabs$ = this.notificationsStoreService.unviewedNotificationsCounters$.pipe(map(couters => {
        return this.baseTabsInfo.map(({ counterKey, ...tabInfo }) => ({ ...tabInfo, badge: { badgeCountAlert: couters[counterKey] } }));
    }));

    public readonly activeTab$ = this.notificationsStoreService.activeTab$.pipe(tap(tab => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.notificationTypeControlOptions = { ...this.notificationTypeControlOptions, selectedValue: tab };
    }));

    public notificationTypeControlOptions: RpcSelectControltOptions;

    constructor(
        private readonly googleAnalyticsStoreService: GoogleAnalyticsStoreService,
        private readonly notificationsStoreService: NotificationsStoreService,
        private readonly matchMediaService: MatchMediaService,
        private readonly hybridService: HybridService,
    ) {
        this.initializeNotificationTypeControl();
    }

    public onShowOnlyUnviewedFilterChanged(value: boolean): void {
        this.googleAnalyticsStoreService.addNotificationBellToggleEvent(value);
        this.notificationsStoreService.showOnlyUnviewedFilterChanged();
    }

    public onSelectedTabChanged({ value }: RpcTabItem<NotificationTab>) {
        this.notificationsStoreService.setActiveTab(value);
    }

    public onNavigateToNotificationEntity(notificationNavigateParams: NotificationNavigateParams): void {
        this.notificationSourceNavigated.emit(notificationNavigateParams);
    }

    public onMarkAllViewed(): void {
        this.notificationsStoreService.markAllViewed();
    }

    public onClose(): void {
        this.closed.emit();
    }

    public onNotificationTypeChanged(option: RpcSelectOption): void {
        this.notificationsStoreService.setActiveTab(option.value);
    }

    private initializeNotificationTypeControl() {
        const options: RpcSelectOption[] = [
            { text: 'NOTIFICATIONS.TITLES.ALL_NOTIFICATIONS', value: NotificationTab.All },
            { text: 'NOTIFICATIONS.TITLES.MESSAGES', value: NotificationTab.Messages },
            { text: 'NOTIFICATIONS.TITLES.APPOINTMENTS', value: NotificationTab.Appointments },
            { text: 'NOTIFICATIONS.TITLES.LISTINGS', value: NotificationTab.Listings },
        ];

        this.notificationTypeControlOptions = new RpcSelectControltOptions(new UntypedFormGroup({}), 'notificationType', options, options[0].value);
    }
}